import type { CorrelationMatrixData } from "@repo/shared/types";

/**
 * QL-655 — the save payload for a correlation grid: one entry per pair, taken
 * from the grid's editable cell `matrix[rowId][colId]` with the row below the
 * column in display order.
 *
 * Only one direction is sent; the API writes it to both. When the editable
 * cell holds no key at all (values that came in through the other direction,
 * e.g. from the server), the other direction is used. A cleared cell is
 * `null` and clears the pair.
 *
 * A grid save sends only the pairs the designer has set (or cleared): pairs
 * nobody filled in are left out rather than sent as 0, so a missing pair stays
 * missing and the forecast names it instead of quietly assuming no
 * correlation.
 *
 * A CSV upload replaces the whole matrix (`clearMissing`): a pair the file
 * leaves blank is sent as `null` and cleared, so blanking a cell in the file
 * is how a pair is cleared when the grid is collapsed.
 */
export function buildCorrelationPayload(
  ids: string[],
  stored: CorrelationMatrixData,
  { clearMissing = false }: { clearMissing?: boolean } = {},
): CorrelationMatrixData {
  const payload: CorrelationMatrixData = {};
  for (let row = 1; row < ids.length; row++) {
    for (let col = 0; col < row; col++) {
      const rowId = ids[row]!;
      const colId = ids[col]!;
      const rowMap = stored[rowId];
      const value = rowMap && colId in rowMap ? rowMap[colId] : stored[colId]?.[rowId];
      if (value === undefined && !clearMissing) continue;
      payload[rowId] ??= {};
      payload[rowId][colId] = value ?? null;
    }
  }
  return payload;
}
